import { Calendar, Clock, Edit2, Trash2, CheckCircle, Circle } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Reminder } from '../lib/api';

interface Props {
  reminder: Reminder;
  onDelete: (id: string) => void;
  onToggle: (id: string) => void;
}

const OFFSET_LABELS: Record<string, string> = {
  day_before: '1 day before',
  week_before: '1 week before',
  month_before: '1 month before',
};

export default function ReminderCard({ reminder, onDelete, onToggle }: Props) {
  const isOverdue = !reminder.is_completed && new Date(reminder.due_date) < new Date();
  const isReminderSoon = !reminder.notification_sent && new Date(reminder.reminder_date) <= new Date();

  return (
    <div className={`card relative transition-all duration-200 ${reminder.is_completed ? 'opacity-60' : ''} ${isOverdue ? 'border-red-200' : ''}`}>
      {isReminderSoon && !reminder.notification_sent && (
        <span className="absolute top-3 right-3 bg-yellow-100 text-yellow-700 text-xs font-semibold px-2 py-0.5 rounded-full">
          Reminder due!
        </span>
      )}
      <div className="flex items-start gap-3">
        <button
          onClick={() => onToggle(reminder.id)}
          className="mt-0.5 text-gray-400 hover:text-blue-600 transition-colors flex-shrink-0"
          title={reminder.is_completed ? 'Mark incomplete' : 'Mark complete'}
        >
          {reminder.is_completed
            ? <CheckCircle className="w-5 h-5 text-green-500" />
            : <Circle className="w-5 h-5" />
          }
        </button>
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold text-gray-900 truncate ${reminder.is_completed ? 'line-through text-gray-400' : ''}`}>
            {reminder.title}
          </h3>
          {reminder.description && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{reminder.description}</p>
          )}
          <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              Due: <strong className={isOverdue ? 'text-red-500' : 'text-gray-700'}>{reminder.due_date}</strong>
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Remind: {reminder.reminder_date} ({OFFSET_LABELS[reminder.reminder_offset]})
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to={`/reminders/${reminder.id}/edit`}
            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </Link>
          <button
            onClick={() => onDelete(reminder.id)}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
